import { useMemo, useState, useDeferredValue } from "react";
import { STATUS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChatDialog } from "@/components/ChatDialog";
import { EvidenceCaptureDialog } from "@/components/EvidenceCaptureDialog";
import { useChatUnread } from "@/lib/useChatUnread";
import { useLiveTracking } from "@/lib/useLiveTracking";
import { useWakeLock } from "@/lib/useWakeLock";
import { MobileHeader } from "./mobile/MobileHeader";
import { MobileSearchAndChips } from "./mobile/MobileSearchAndChips";
import { MobileStatusPrompt } from "./mobile/MobileStatusPrompt";
import { buildPedidoLabel } from "./mobile/mobileHelpers";
import { TrocoSaidaBanner, TrocoSaidaDialog } from "./mobile/MobileTrocoSaida";
import { MobileChangePasswordDialog } from "./mobile/MobileChangePasswordDialog";
import { MobileDesempenhoDialog } from "./mobile/MobileDesempenhoDialog";
import { ConferenciaBarcodeDialog } from "./mobile/ConferenciaBarcodeDialog";
import { MobileRotaBar } from "./mobile/MobileRotaBar";
import { MobileTrackingBlock } from "./mobile/MobileTrackingBlock";
import { ConfirmarTodasBanner } from "./mobile/ConfirmarTodasBanner";
import { ConfirmarRecebimentoDialog } from "./mobile/ConfirmarRecebimentoDialog";
import { MobileNoLink } from "./mobile/MobileNoLink";
import { MobileEntregasList } from "./mobile/MobileEntregasList";
import { useMinhasEntregas } from "./mobile/useMinhasEntregas";
import { useFlowBusy } from "./mobile/useFlowBusy";
import { useStatusFlow } from "./mobile/useStatusFlow";
import { useRotaOrdem } from "./mobile/useRotaOrdem";
import { useTrocoSaida } from "./mobile/useTrocoSaida";
import { useConferencia } from "./mobile/useConferencia";
import { computeStatusCounts, computeEmpresasDisponiveis, filterAndSortEntregas, emRota } from "./mobile/mobileListHelpers";

// App do entregador (PWA). A lógica fica nos hooks em ./mobile/*: dados+polling (useMinhasEntregas),
// fluxo de status (useStatusFlow), conferência/aceite (useConferencia), troco de saída (useTrocoSaida),
// sequência da rota (useRotaOrdem). Esta página só compõe.
export default function MobileEntregador() {
  const { user, logout } = useAuth();
  const doLogout = () => { logout(); window.location.href = "/login"; };

  const data = useMinhasEntregas();
  const { items, setItems, lookup, cfg, loading, load } = data;
  const flow = useFlowBusy(data.mountedRef);
  const status = useStatusFlow({ data, flow });
  const troco = useTrocoSaida({ items, setItems, load });
  const conf = useConferencia({ items, setItems, load, troco });

  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);  // mantém o Input responsivo com 200+ entregas
  const [filterStatus, setFilterStatus] = useState("pendentes");
  const [filterEmpresa, setFilterEmpresa] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [chatEntrega, setChatEntrega] = useState(null);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [desempenhoOpen, setDesempenhoOpen] = useState(false);
  const { unreadMap, markRead } = useChatUnread();

  // Rastreamento GPS ao vivo enquanto houver entrega em_transito de empresa com rastreio ativo.
  const trackingEmpresas = useMemo(() => {
    const map = cfg?.tracking_ativo_by_empresa;
    if (!map) return null;
    return new Set(Object.entries(map).filter(([, v]) => v).map(([k]) => k));
  }, [cfg]);
  const { tracking: liveTracking, queueSize: pingQueueSize, online: netOnline } = useLiveTracking(items, { enabledEmpresaIds: trackingEmpresas });
  const { supported: wakeLockSupported, locked: wakeLockActive } = useWakeLock(liveTracking);

  const statusCounts = useMemo(() => computeStatusCounts(items), [items]);
  const empresasDisponiveis = useMemo(() => computeEmpresasDisponiveis(items), [items]);
  const filtered = useMemo(
    () => filterAndSortEntregas(items, { search: deferredSearch, filterStatus, filterEmpresa, lookup }),
    [items, deferredSearch, filterStatus, filterEmpresa, lookup],
  );
  const ativosOrdenados = useMemo(() => filtered.filter(emRota), [filtered]);
  const rota = useRotaOrdem(ativosOrdenados, setItems);
  const totalUnread = useMemo(() => Object.values(unreadMap).reduce((a, v) => a + (v.count || 0), 0), [unreadMap]);

  const openChat = (it) => { markRead(it.id); setChatEntrega(it); };
  const toggleExpanded = (id, isCollapsed) => setExpanded((prev) => ({ ...prev, [id]: !!isCollapsed }));

  if (!user?.entregador_id) return <MobileNoLink onLogout={doLogout} />;

  return (
    <div className="min-h-screen bg-orange-50/40 pb-8" data-testid="page-mobile">
      <MobileHeader
        userNome={user.nome}
        unreadCount={Object.keys(unreadMap).length}
        totalUnread={totalUnread}
        onOpenAllUnread={() => { setFilterStatus("todas"); setSearch(""); }}
        onReload={load}
        loading={loading}
        onLogout={doLogout}
        onChangePassword={() => setChangePwdOpen(true)}
        onOpenDesempenho={() => setDesempenhoOpen(true)}
      />
      <MobileChangePasswordDialog open={changePwdOpen} onOpenChange={setChangePwdOpen} />
      <MobileDesempenhoDialog open={desempenhoOpen} onOpenChange={setDesempenhoOpen} />

      <div className="p-4 space-y-4 max-w-2xl mx-auto">
        <div className="text-center">
          <div className="text-4xl font-extrabold text-orange-700" data-testid="mobile-count-visible">{filtered.length}</div>
          <div className="text-sm text-muted-foreground">
            {search || filterStatus !== "pendentes" ? `de ${items.length} entrega${items.length !== 1 ? "s" : ""} hoje` : "Suas entregas de hoje"}
          </div>
          {liveTracking && (
            <MobileTrackingBlock wakeLockSupported={wakeLockSupported} wakeLockActive={wakeLockActive} netOnline={netOnline} pingQueueSize={pingQueueSize} />
          )}
        </div>

        <MobileSearchAndChips
          search={search} setSearch={setSearch}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          statusCounts={statusCounts}
          empresas={empresasDisponiveis} filterEmpresa={filterEmpresa} setFilterEmpresa={setFilterEmpresa}
        />

        <MobileRotaBar ativos={ativosOrdenados.length} onOtimizar={rota.otimizarRota} busy={rota.rotaBusy} resumo={rota.rotaResumo} />

        <TrocoSaidaBanner pendentes={troco.pendentes} onOpen={() => troco.setDialogOpen(true)} />

        {statusCounts.aguardando_conferencia > 0 && (
          <ConfirmarTodasBanner
            count={statusCounts.aguardando_conferencia}
            onLookup={(t) => conf.handleConferenciaLookup(t, "search")}
            onOpenScanner={() => conf.setScanOpen(true)}
            onConfirmarTodas={conf.askConfirmarTodas}
            disabled={conf.savingReceipt}
          />
        )}

        <MobileEntregasList
          filtered={filtered} total={items.length} ativosOrdenados={ativosOrdenados}
          expanded={expanded} onToggleExpanded={toggleExpanded}
          data={data} unreadMap={unreadMap} flowBusy={flow.flowBusy} rota={rota}
          actions={{ openChat, changeFormaPgto: status.changeFormaPgto, setStatus: status.setStatus, askConfirmarRecebimento: conf.askConfirmarRecebimento }}
        />
      </div>

      <MobileStatusPrompt prompt={status.prompt} setPrompt={status.setPrompt} onConfirm={status.confirmPrompt} saving={status.saving} flowBusy={flow.flowBusy} />

      <ConfirmarRecebimentoDialog
        confirmReceipt={conf.confirmReceipt} saving={conf.savingReceipt}
        onClose={() => conf.setConfirmReceipt(null)} onToggleTroco={conf.toggleTrocoOk} onConfirm={conf.doConfirmarRecebimento}
      />

      <TrocoSaidaDialog open={troco.dialogOpen} entregas={troco.pendentes} onClose={() => troco.setDialogOpen(false)} onConfirm={troco.confirmar} saving={troco.saving} />

      <ConferenciaBarcodeDialog open={conf.scanOpen} onOpenChange={conf.onScannerOpenChange} onScan={(text) => conf.handleConferenciaLookup(text, "scan")} />

      <ChatDialog open={!!chatEntrega} onOpenChange={(o) => !o && setChatEntrega(null)} entrega={chatEntrega} currentUserId={user?.id}
        title={`Falar sobre pedido ${chatEntrega ? buildPedidoLabel(chatEntrega, lookup.tip[chatEntrega.tipo_id], "—") : "—"}`} />
      <EvidenceCaptureDialog
        open={!!status.evidencePending}
        onOpenChange={(o) => { if (!o) status.closeEvidence(); }}
        entregaId={status.evidencePending?.entrega?.id}
        statusLabel={status.evidencePending?.status ? STATUS[status.evidencePending.status] : ""}
        onUploaded={status.onEvidenceUploaded}
      />
    </div>
  );
}
